import { z } from 'zod';

/**
 * A file on a job, as the list endpoint returns it.
 *
 * `r2Key` is the display rendition every in-app surface reads. `originalKey` is
 * the untouched uploaded bytes and is null until the original lands — the display
 * rendition uploads first and the original drains from the queue behind it, so it
 * stays null permanently if the user navigates away mid-batch. `contentType` and
 * `sizeBytes` are null on rows the backfill created from an R2 HEAD that returned
 * neither.
 */
export const jobFileEntrySchema = z.object({
  id: z.string().uuid(),
  r2Key: z.string().min(1),
  thumbKey: z.string().nullable(),
  originalKey: z.string().nullable(),
  fileName: z.string().min(1),
  contentType: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  originalSizeBytes: z.number().int().nonnegative().nullable(),
  /** The folder holding this file; null means it sits at the job root. */
  folderId: z.string().uuid().nullable(),
  tags: z.array(z.string()),
  description: z.string().nullable(),
  uploadedByName: z.string().nullable(),
  createdAt: z.string(),
  /**
   * The two facts every Expenses-folder label is derived from. Nothing about a
   * file's status is stored — see `jobFileAttachment`.
   */
  isPending: z.boolean(),
  hasExpense: z.boolean(),
});
export type JobFileEntry = z.infer<typeof jobFileEntrySchema>;

/** What an Expenses-folder file is, or null for a file that isn't one. */
export type JobFileAttachment = 'pending' | 'attached' | 'unattached';

/**
 * Whether a file is queued, attached to an expense, or neither.
 *
 * Derived rather than stored, so the Files tab and the Expenses tab cannot
 * disagree and no status column can drift out of step with the references it
 * claims to describe. Deleting an expense leaves the file Unattached rather
 * than re-queueing it: it is no longer work, it is just a file.
 */
export function jobFileAttachment(
  file: Pick<JobFileEntry, 'folderId' | 'isPending' | 'hasExpense'>,
  expensesFolderId: string | null,
): JobFileAttachment | null {
  if (!expensesFolderId || file.folderId !== expensesFolderId) return null;
  // A queue row is outstanding work, which outranks an existing attachment in
  // the (unexpected) case that a file carries both.
  if (file.isPending) return 'pending';
  if (file.hasExpense) return 'attached';
  return 'unattached';
}

// Metadata edit limits. Named so the edit form enforces the same numbers the
// route does, rather than a second copy that drifts.
export const MAX_JOB_FILE_NAME_LENGTH = 255;
export const MAX_JOB_FILE_TAGS = 20;
export const MAX_JOB_FILE_TAG_LENGTH = 50;
export const MAX_JOB_FILE_DESCRIPTION_LENGTH = 1000;

/**
 * Metadata edit. Deliberately no `folderId`.
 *
 * Moving a file is guarded — refused while an expense references it, and
 * refused outright for a folder belonging to another job. Accepting a folder
 * here would be a second way in that enforces neither, so there is exactly one
 * path and it is PATCH /files/:id/folder.
 *
 * Strict, so sending one is a 400 rather than a 200 that quietly did not move
 * the file — a silent no-op is the harder version of this mistake to find.
 */
export const updateJobFileSchema = z
  .object({
    fileName: z.string().min(1).max(MAX_JOB_FILE_NAME_LENGTH).optional(),
    tags: z.array(z.string().min(1).max(MAX_JOB_FILE_TAG_LENGTH)).max(MAX_JOB_FILE_TAGS).optional(),
    description: z.string().max(MAX_JOB_FILE_DESCRIPTION_LENGTH).nullable().optional(),
  })
  .strict();
export type UpdateJobFileInput = z.infer<typeof updateJobFileSchema>;

export const jobFileListQuerySchema = z.object({
  /**
   * Absent lists the whole job; null lists the job root; a uuid lists one folder.
   *
   * Query strings can't carry a null, so the literal `'null'` stands in for it —
   * "the job root" and "anywhere on the job" are different questions and both
   * have to be askable over HTTP.
   */
  folderId: z.preprocess(
    (value) => (value === 'null' ? null : value),
    z.string().uuid().nullable().optional(),
  ),
  q: z.string().optional(),
});
export type JobFileListQuery = z.infer<typeof jobFileListQuerySchema>;

/**
 * Query for the job-photos endpoint: how many thumbnails to presign.
 *
 * Bounds `thumbUrls` only — the response still carries every photo on the job,
 * because the same request feeds the lightbox, which pages the lot.
 *
 * The numbers: 6 is what the hover popover's grid shows, so it is the default a
 * caller that says nothing gets. 24 is the ceiling, and it is a contract with
 * the frontend rather than a guess — a caller asking for more gets a 400, not a
 * clamped response, so a popover redesign that wants a bigger grid has to raise
 * this deliberately instead of silently receiving fewer thumbnails than it drew
 * slots for. 0 is allowed because "the full photo set, no thumbnails" is a real
 * ask: the lightbox opened directly needs `photos` and nothing else.
 *
 * `z.coerce` is load-bearing: hono hands a query validator strings, so a bare
 * `z.number()` would reject every request that passes a limit at all.
 */
export const jobPhotosQuerySchema = z.object({
  thumbLimit: z.coerce.number().int().min(0).max(24).default(6),
});
export type JobPhotosQuery = z.infer<typeof jobPhotosQuerySchema>;

/** 20MB per file, matching the existing receipt upload route. */
export const MAX_JOB_FILE_BYTES = 20 * 1024 * 1024;
/** Files accepted in a single client-side upload batch. */
export const MAX_JOB_FILE_BATCH = 50;

// ---------------------------------------------------------------------------
// Upload allowlist
//
// Lives here rather than in the upload route because two places now enforce it:
// the multipart route, which holds the bytes, and the presigned-original flow,
// which never sees them and must bake the rules into the signature instead.
// ---------------------------------------------------------------------------

/** Document types a job accumulates. Any `image/*` is allowed on top of these. */
export const ALLOWED_JOB_FILE_CONTENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

/**
 * Image extensions that stand in for a missing content type.
 *
 * Browsers — iOS Safari especially — hand over an empty type for HEIC often
 * enough that a type-only allowlist would reject genuine photos.
 *
 * Not upload-only: `isImageEntry` falls back on these keys, so this list is also
 * half of "what counts as a photo". Its SQL twin is the extension alternation in
 * `backend/src/db/jobFileImagePredicate.ts` — add an extension here and you must
 * add it there too, or the job list's photo count silently under-reports and
 * those photos become unreachable from the Photos column. A test in
 * `backend/src/db/__tests__/jobFileImagePredicate.test.ts` fails if they drift.
 */
export const TYPELESS_IMAGE_EXTENSIONS: Record<string, string> = {
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
};

/** Lowercased extension of a file name, including the dot; '' when it has none. */
export function jobFileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(dot).toLowerCase() : '';
}

/** The image type an extension implies, for uploads that arrive without one. */
export function contentTypeForExtension(ext: string): string | null {
  return TYPELESS_IMAGE_EXTENSIONS[ext.toLowerCase()] ?? null;
}

/**
 * Document extensions that stand in for a missing content type.
 *
 * Deliberately separate from TYPELESS_IMAGE_EXTENSIONS, which is also half of
 * "what counts as a photo" and is mirrored in SQL. Putting `.pdf` in there would
 * make a typeless PDF a candidate photo and break that mirror; keeping it here
 * only widens what an upload is allowed to be.
 *
 * Needed because browsers report an empty type for a PDF too — less often than
 * for HEIC, but a receipt the user scans into Files can arrive that way, and the
 * type-only check rejected it.
 */
export const TYPELESS_DOCUMENT_EXTENSIONS: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/**
 * What to store a typeless upload as, across every type the library accepts.
 *
 * `contentTypeForExtension` answers the narrower question "which image is this
 * extension" and must stay image-only for its SQL mirror's sake. Storage wants
 * the broader answer, or a typeless PDF lands as octet-stream and the viewer
 * can't pick a renderer for it.
 */
export function storedContentTypeForExtension(ext: string): string | null {
  const key = ext.toLowerCase();
  return TYPELESS_IMAGE_EXTENSIONS[key] ?? TYPELESS_DOCUMENT_EXTENSIONS[key] ?? null;
}

/**
 * The extension a content type implies, or null when it implies none.
 *
 * The presigned-original flow needs this: the client downscales a HEIC to a
 * .jpg and stores it under that name, so the row's file name says nothing about
 * what the original actually is. The type the browser declares for the original
 * does.
 */
const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  'image/heic': '.heic',
  'image/heif': '.heif',
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'application/pdf': '.pdf',
};

export function extensionForContentType(contentType: string | null | undefined): string | null {
  const type = (contentType ?? '').trim().toLowerCase();
  return CONTENT_TYPE_EXTENSIONS[type] ?? null;
}

/** Whether an upload is one of the types the library accepts. */
export function isAllowedJobFileUpload(input: {
  contentType: string | null | undefined;
  fileName: string;
}): boolean {
  const type = (input.contentType ?? '').trim().toLowerCase();
  if (type.startsWith('image/')) return true;
  if ((ALLOWED_JOB_FILE_CONTENT_TYPES as readonly string[]).includes(type)) return true;
  // A type that says nothing leaves the extension to decide.
  if (!type || type === 'application/octet-stream') {
    return storedContentTypeForExtension(jobFileExtension(input.fileName)) !== null;
  }
  return false;
}

// ---------------------------------------------------------------------------
// What a file is: photo, PDF, or neither
//
// Both the UI and the backend ask this question, and they must not answer it
// differently. The frontend filters in TS; the backend reads isImageEntry
// directly in listJobPhotos; and JOB_FILE_IMAGE_PREDICATE
// (backend/src/db/jobFileImagePredicate.ts) mirrors it in SQL for the job
// list's photo count. Any change here has to land in that mirror too.
//
// Note the deliberate lack of .trim() on the content-type comparisons, unlike
// isAllowedJobFileUpload above: the SQL mirror uses a bare lower(content_type),
// and trimming on one side only would recreate the divergence this exists to
// prevent.
// ---------------------------------------------------------------------------

/**
 * How a job file is presented — read by the frontend's grid and list view and
 * by the backend — so none of them can drift into calling the same file
 * different things.
 */
export type DisplayFile = Pick<JobFileEntry, 'contentType' | 'thumbKey' | 'fileName'>;

/**
 * Whether an entry is a PDF.
 *
 * Content type first, extension second, for the same reason `iconForJobFile`
 * (frontend/src/pages/jobs/components/files/jobFileDisplay.ts) checks both: a
 * backfilled row can carry no content type at all, and a scanned receipt PDF is
 * exactly the entry that has to reach the PDF viewer rather than the no-preview
 * message.
 */
export function isPdfEntry(file: DisplayFile): boolean {
  if ((file.contentType ?? '').toLowerCase() === 'application/pdf') return true;
  return jobFileExtension(file.fileName) === '.pdf';
}

/**
 * Whether an entry *is a photo* — something that can be paged through in the
 * lightbox and dropped into a report slot.
 *
 * A thumbnail is a strong signal but not proof, and the exception is the whole
 * point: the file-store backfill renders a first-page thumbnail for PDFs as
 * well as images, so every backfilled receipt PDF carries one. Answering "yes"
 * for those sent them to the photo lightbox, which renders an `<img>` of the
 * display rendition and shows nothing, and made them selectable in the report
 * photo picker under a tooltip reading "Only photos can go in a report slot."
 *
 * PDFs are therefore excluded up front. Use `hasThumbnail`
 * (frontend/src/pages/jobs/components/files/jobFileDisplay.ts) if what you
 * actually want is "is there a raster to draw", which for a PDF is yes.
 *
 * The extension is the fallback for backfilled rows, whose R2 HEAD may have
 * returned no content type at all.
 */
export function isImageEntry(file: DisplayFile): boolean {
  if (isPdfEntry(file)) return false;
  if (file.contentType?.toLowerCase().startsWith('image/')) return true;
  if (file.thumbKey) return true;
  return file.contentType == null
    && contentTypeForExtension(jobFileExtension(file.fileName)) !== null;
}

// ---------------------------------------------------------------------------
// Presigned original upload
//
// Full-quality originals are PUT straight to R2 by the browser so 20MB × 50
// files never flow through the API. The API issues the URL and records the key
// afterwards; it is never in the byte path.
// ---------------------------------------------------------------------------

/** Prefix every presigned original key is minted under. */
export const JOB_FILE_ORIGINAL_PREFIX = 'job-files/originals/';

/**
 * Shape of a key this API issued.
 *
 * The client hands the key back after its PUT, so it is user input: without
 * pinning the shape, `original_key` could be pointed at any object in the
 * bucket and read back through the download route.
 */
export const JOB_FILE_ORIGINAL_KEY_PATTERN =
  /^job-files\/originals\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\.[a-z0-9]{1,8})?$/;

/** Body of the presign request. `contentType` may be '' — iOS HEIC sends nothing. */
export const jobFileOriginalUploadRequestSchema = z.object({
  contentType: z.string().max(255),
  sizeBytes: z.number().int().positive().max(MAX_JOB_FILE_BYTES),
});
export type JobFileOriginalUploadRequest = z.infer<typeof jobFileOriginalUploadRequestSchema>;

export const jobFileOriginalUploadResponseSchema = z.object({
  uploadUrl: z.string().min(1),
  key: z.string().min(1),
  /**
   * The content type the signature was cut against. The browser must send this
   * header verbatim — a Blob with no type would otherwise omit it and the PUT
   * would fail the signature check.
   */
  contentType: z.string().min(1),
});
export type JobFileOriginalUploadResponse = z.infer<typeof jobFileOriginalUploadResponseSchema>;

/** Recorded once the browser's PUT to R2 succeeds. */
export const recordJobFileOriginalSchema = z.object({
  originalKey: z.string().regex(JOB_FILE_ORIGINAL_KEY_PATTERN, 'Not a job file original key'),
  originalSizeBytes: z.number().int().nonnegative().max(MAX_JOB_FILE_BYTES),
});
export type RecordJobFileOriginalInput = z.infer<typeof recordJobFileOriginalSchema>;

// ---------------------------------------------------------------------------
// Delete guard
//
// Report slots reference a file by id inside a jsonb document, which no foreign
// key can reach into, so deleting a file a report uses would leave that report
// rendering a gap.
// The delete route refuses with a 409 in this shape, and the UI names the
// reports so clearing the slots is an obvious next step rather than a dead end.
// ---------------------------------------------------------------------------

export const JOB_FILE_IN_USE_CODE = 'JOB_FILE_IN_USE' as const;

/**
 * A move refused because an expense references the file.
 *
 * The lock is a fact about the reference, not the folder: deleting the expense
 * releases it. Named here so the route and the dialog can't disagree.
 */
export const JOB_FILE_LOCKED_CODE = 'JOB_FILE_LOCKED' as const;

/** A report standing in the way of a delete, in the form the UI names it by. */
export const jobFileInUseReportSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
});
export type JobFileInUseReport = z.infer<typeof jobFileInUseReportSchema>;

/**
 * The 409 body.
 *
 * `reports` is non-empty by construction: the whole point of this response is
 * naming what is in the way, and a body that names nothing is an ordinary
 * failure the UI should report as one.
 */
export const jobFileInUseResponseSchema = z.object({
  error: z.string(),
  code: z.literal(JOB_FILE_IN_USE_CODE),
  reports: z.array(jobFileInUseReportSchema).min(1),
});
export type JobFileInUseResponse = z.infer<typeof jobFileInUseResponseSchema>;

/**
 * An expense standing in the way of a delete, in the form the UI names it by.
 *
 * Enough to recognise the expense on the Expenses tab: the remedy is deleting
 * it or giving it a different receipt, and neither is findable from an id.
 */
export const jobFileExpenseUsageSchema = z.object({
  id: z.string().min(1),
  storeName: z.string().min(1),
  amount: z.number(),
  expenseDate: z.string().min(1),
});
export type JobFileExpenseUsage = z.infer<typeof jobFileExpenseUsageSchema>;

/**
 * The 409 body for a file an expense holds.
 *
 * Shares {@link JOB_FILE_LOCKED_CODE} with the refused *move*: it is the same
 * fact about the same reference, and the same thing releases it. The move sends
 * no `expenses`, so a body without them parses as an ordinary failure rather
 * than as a blocked dialog listing nothing.
 */
export const jobFileLockedResponseSchema = z.object({
  error: z.string(),
  code: z.literal(JOB_FILE_LOCKED_CODE),
  expenses: z.array(jobFileExpenseUsageSchema).min(1),
});
export type JobFileLockedResponse = z.infer<typeof jobFileLockedResponseSchema>;
